import { ServiceError } from "../../service/Shared";
import { normalizeStage } from "../workflow/normalizeStage";

const ONGOING_MONITORING_PHASE_KEY = normalizeStage("ongoingMonitoring");

const PROJECT_PHASE_BOUNDARY_FIELDS: Record<string, { start: string; end: string }> = {
  feasibility: { start: "feasibility.start", end: "feasibility.end" },
  design: { start: "design.start", end: "design.end" },
  implementation: { start: "implementation.start", end: "implementation.end" },
  ongoingMonitoring: { start: "ongoingMonitoring.overallStart", end: "ongoingMonitoring.overallEnd" },
};

const PROJECT_KEY_DATE_FIELD_MAP: Record<string, Record<string, string>> = {
  design: {
    registrationdate: "design.registrationDate",
  },
  implementation: {
    implementationdate: "implementation.implementationDate",
    projectimplementationdate: "implementation.implementationDate",
    certificationdate: "implementation.certificationDate",
    startdate: "implementation.startDate",
    projectstartdate: "implementation.startDate",
    validationdate: "implementation.validationDate",
    restorationvalidationdate: "implementation.restorationValidationDate",
  },
  ongoingMonitoring: {
    finalassessmentdate: "ongoingMonitoring.finalAssessmentDate",
    secondassessmentdate: "ongoingMonitoring.cycles[0].assessmentDate",
  },
};

const PROJECT_KEY_DATE_ORDER_FIELD_MAP: Record<string, string> = {
  [normalizeStage("Feasibility phase start date")]: "feasibility.start",
  [normalizeStage("Feasibility phase end date")]: "feasibility.end",
  [normalizeStage("Design phase start date")]: "design.start",
  [normalizeStage("Design phase end date")]: "design.end",
  [normalizeStage("Implementation phase start date")]: "implementation.start",
  [normalizeStage("Implementation phase end date")]: "implementation.end",
  [normalizeStage("Ongoing monitoring phase start date")]: "ongoingMonitoring.overallStart",
  [normalizeStage("Ongoing monitoring phase end date")]: "ongoingMonitoring.overallEnd",
};

const PROJECT_ERROR_CODE_FIELD_MAP: Record<string, string[]> = {
  VALIDATION_START_DATE_AFTER_IMPLEMENTATION_DATE: ["implementation.implementationDate", "implementation.startDate"],
  VALIDATION_VALIDATION_DATE_AFTER_START_DATE: ["implementation.startDate", "implementation.validationDate"],
  VALIDATION_VALIDATION_DATE_AFTER_RESTORATION_DATE: [
    "implementation.validationDate",
    "implementation.restorationValidationDate",
  ],
  VALIDATION_SECOND_ASSESSMENT_DATE_AFTER_VALIDATION_DATE: [
    "implementation.validationDate",
    "ongoingMonitoring.cycles[0].assessmentDate",
  ],
  VALIDATION_FINAL_ASSESSMENT_DATE_AFTER_SECOND_ASSESSMENT_DATE: [
    "ongoingMonitoring.cycles[0].assessmentDate",
    "ongoingMonitoring.finalAssessmentDate",
  ],
};

const getCycleFieldKey = (label: string | null | undefined): string | null => {
  const normalizedLabel = normalizeStage(label);
  const cycleMatch = normalizedLabel.match(/^cycle(\d+)(startdate|enddate|assessmentdate)$/);

  if (!cycleMatch) return null;

  const cycleIndex = Number(cycleMatch[1]) - 1;
  if (cycleIndex < 0) return null;

  const cycleFieldMap: Record<string, string> = {
    startdate: "start",
    enddate: "end",
    assessmentdate: "assessmentDate",
  };

  const fieldKey = cycleFieldMap[cycleMatch[2]];
  return fieldKey ? `ongoingMonitoring.cycles[${cycleIndex}].${fieldKey}` : null;
};

const getPhaseBoundaryFields = (phase: string | null | undefined): { start: string; end: string } | null => {
  const normalizedPhase = normalizeStage(phase);
  if (!normalizedPhase) return null;

  const cycleMatch = normalizedPhase.match(/^cycle(\d+)$/);
  if (cycleMatch) {
    const cycleIndex = Number(cycleMatch[1]) - 1;

    if (cycleIndex < 0) return null;

    return {
      start: `ongoingMonitoring.cycles[${cycleIndex}].start`,
      end: `ongoingMonitoring.cycles[${cycleIndex}].end`,
    };
  }

  return (
    Object.entries(PROJECT_PHASE_BOUNDARY_FIELDS).find(
      ([phaseKey]) => normalizeStage(phaseKey) === normalizedPhase
    )?.[1] ?? null
  );
};

const getPhaseKeyDateField = (phase: string | null | undefined, keyLabel: string | null | undefined): string | null => {
  const normalizedPhase = normalizeStage(phase);
  const normalizedKeyLabel = normalizeStage(keyLabel);

  if (!normalizedPhase || !normalizedKeyLabel) return null;

  const cycleMatch = normalizedPhase.match(/^cycle(\d+)$/);
  if (cycleMatch && normalizedKeyLabel === normalizeStage("Assessment date")) {
    const cycleIndex = Number(cycleMatch[1]) - 1;
    return cycleIndex >= 0 ? `ongoingMonitoring.cycles[${cycleIndex}].assessmentDate` : null;
  }

  const phaseEntry = Object.entries(PROJECT_KEY_DATE_FIELD_MAP).find(
    ([phaseKey]) => normalizeStage(phaseKey) === normalizedPhase
  );

  if (!phaseEntry) return null;

  const [, keyDateMap] = phaseEntry;
  return Object.entries(keyDateMap).find(([labelKey]) => normalizeStage(labelKey) === normalizedKeyLabel)?.[1] ?? null;
};

const getProjectKeyDateOrderFields = (error: ServiceError): string[] => {
  const keyDate1 =
    getCycleFieldKey(error.parameters?.keyDate1) ??
    PROJECT_KEY_DATE_ORDER_FIELD_MAP[normalizeStage(error.parameters?.keyDate1)];
  const keyDate2 =
    getCycleFieldKey(error.parameters?.keyDate2) ??
    PROJECT_KEY_DATE_ORDER_FIELD_MAP[normalizeStage(error.parameters?.keyDate2)];

  return [keyDate1, keyDate2].filter((fieldKey): fieldKey is string => Boolean(fieldKey));
};

const getProjectDateErrorFieldKeys = (error: ServiceError): string[] => {
  const codeMappedFields = PROJECT_ERROR_CODE_FIELD_MAP[error.code];
  if (codeMappedFields) {
    return codeMappedFields;
  }

  if (error.code === "VALIDATION_END_DATE_AFTER_START_DATE") {
    const phaseBoundaryFields = getPhaseBoundaryFields(error.property);
    return phaseBoundaryFields ? [phaseBoundaryFields.start, phaseBoundaryFields.end] : [];
  }

  if (error.code === "VALIDATION_KEY_DATE_NOT_BETWEEN_START_END_DATE") {
    const phase = error.parameters?.phase ?? error.property;
    const keyLabel = error.parameters?.key;
    const keyDateField = getPhaseKeyDateField(phase, keyLabel);

    if (!keyDateField) return [];

    const phaseBoundaryFields = getPhaseBoundaryFields(phase);
    return phaseBoundaryFields ? [phaseBoundaryFields.start, phaseBoundaryFields.end, keyDateField] : [keyDateField];
  }

  if (error.code === "VALIDATION_INVALID_KEY_DATE_ORDER") {
    return getProjectKeyDateOrderFields(error);
  }

  return [];
};

export const getProjectDateValidationErrorFieldKeys = (errors: ServiceError[] | undefined): Set<string> =>
  new Set(
    (errors ?? [])
      .flatMap((error) => getProjectDateErrorFieldKeys(error))
      .map((fieldKey) => {
        const normalizedFieldKey = normalizeStage(fieldKey);
        if (!normalizedFieldKey.startsWith(ONGOING_MONITORING_PHASE_KEY)) {
          return fieldKey;
        }

        return fieldKey;
      })
  );

export const hasProjectDateValidationError = (errorFieldKeys: Set<string>, fieldKey: string): boolean =>
  errorFieldKeys.has(fieldKey);
